import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";

const AVAILABILITY_LABEL = {
  available: "Available",
  sold: "Sold",
  reserved: "Reserved",
  nfs: "Not for sale",
};

export default function ArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInquiry, setShowInquiry] = useState(false);

  useEffect(() => {
    api
      .get(`/artworks/${id}`)
      .then((r) => setArt(r.data))
      .catch(() => setError("Artwork not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-500" data-testid="artwork-loading">
        Lighting the spotlight…
      </div>
    );
  }
  if (error || !art) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="artwork-not-found">
        <p className="font-serif-display text-3xl mb-4">This piece is not on view.</p>
        <Link to="/" className="text-[#D4AF37] underline">Return to the gallery</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-12" data-testid="artwork-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="plaque-label hover:text-white transition-colors mb-12"
        data-testid="artwork-back-button"
      >
        ← Back to the hall
      </button>

      <div className="grid md:grid-cols-12 gap-12 lg:gap-20">
        {/* Image */}
        <motion.div
          className="md:col-span-7 md:sticky md:top-32 self-start"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative">
            <div className="spotlight-pool" />
            <div className="relative art-frame overflow-hidden bg-[#141414]">
              <img
                src={art.image_data}
                alt={art.title}
                className="w-full h-auto object-contain max-h-[80vh]"
                data-testid="artwork-image"
              />
            </div>
          </div>
        </motion.div>

        {/* Curator card */}
        <motion.div
          className="md:col-span-5"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          <p className="plaque-label">Curator's Plaque</p>
          <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl leading-none mt-3" data-testid="artwork-title">
            {art.title}
          </h1>
          {art.year && (
            <p className="mt-3 text-zinc-400 italic font-serif-display text-2xl" data-testid="artwork-year">
              {art.year}
            </p>
          )}

          <div className="divider-thin my-8" />

          {/* Bento metadata */}
          <div className="grid grid-cols-2 gap-px bg-white/5 border border-white/5">
            {art.medium && (
              <div className="bg-[#0F0F0F] p-5">
                <p className="plaque-label mb-2">Medium</p>
                <p className="text-sm text-zinc-200" data-testid="artwork-medium">{art.medium}</p>
              </div>
            )}
            {art.dimensions && (
              <div className="bg-[#0F0F0F] p-5">
                <p className="plaque-label mb-2">Dimensions</p>
                <p className="text-sm text-zinc-200" data-testid="artwork-dimensions">{art.dimensions}</p>
              </div>
            )}
            <div className="bg-[#0F0F0F] p-5">
              <p className="plaque-label mb-2">Status</p>
              <p className="text-sm text-[#D4AF37]" data-testid="artwork-availability">
                {AVAILABILITY_LABEL[art.availability] || art.availability}
              </p>
            </div>
            {art.price && (
              <div className="bg-[#0F0F0F] p-5">
                <p className="plaque-label mb-2">Price</p>
                <p className="text-sm text-zinc-200" data-testid="artwork-price">{art.price}</p>
              </div>
            )}
          </div>

          {art.description && (
            <div className="mt-10">
              <p className="plaque-label mb-3">Description</p>
              <p className="text-zinc-300 leading-relaxed" data-testid="artwork-description">{art.description}</p>
            </div>
          )}

          {art.artist_statement && (
            <div className="mt-10">
              <p className="plaque-label mb-3">Artist's Statement</p>
              <p className="text-zinc-300 leading-relaxed italic font-serif-display text-lg" data-testid="artwork-statement">
                "{art.artist_statement}"
              </p>
            </div>
          )}

          {art.tags && art.tags.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2" data-testid="artwork-tags">
              {art.tags.map((t) => (
                <span key={t} className="text-xs tracking-widest uppercase border border-white/10 px-3 py-1 text-zinc-400">
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Inquiry CTA */}
          <div className="mt-12 border-t border-white/5 pt-8">
            {!showInquiry ? (
              <button
                onClick={() => setShowInquiry(true)}
                className="group inline-flex items-center gap-3 text-sm tracking-[0.3em] uppercase border border-[#D4AF37]/40 hover:border-[#D4AF37] px-6 py-4 text-[#D4AF37] transition-colors"
                data-testid="open-inquiry-button"
              >
                <span>Contact the artist about this work</span>
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </button>
            ) : (
              <InquiryForm
                artworkId={art.id}
                artworkTitle={art.title}
                onSuccess={() => setShowInquiry(false)}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
